// WebsitePopup.js
import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    TextField,
    useTheme,
} from '@mui/material';

// --- Icons ---
import LanguageIcon from '@mui/icons-material/Language';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
    accent: '#3b82f6',
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textTertiary: '#94a3b8',
};

// --- Account Type Card Selector ---
const AccountTypeSelector = ({ value, onChange }) => {
    const options = [
        { key: 'individual', label: 'Individual', description: 'Personal use', icon: <PersonOutlineIcon sx={{ fontSize: 24 }} /> },
        { key: 'business', label: 'Business', description: 'Teams & companies', icon: <BusinessCenterOutlinedIcon sx={{ fontSize: 24 }} /> },
    ];

    return (
        <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
            {options.map((opt) => {
                const selected = value === opt.key;
                return (
                    <Box
                        key={opt.key}
                        onClick={() => onChange(opt.key)}
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 0.5,
                            p: 2,
                            position: 'relative',
                            borderRadius: '14px',
                            cursor: 'pointer',
                            border: '2px solid',
                            borderColor: selected ? colors.accent : 'rgba(0,0,0,0.08)',
                            bgcolor: selected ? colors.accent : 'rgba(255,255,255,0.7)',
                            color: selected ? '#fff' : colors.textPrimary,
                            backdropFilter: 'blur(8px)',
                            transition: 'all 0.25s ease',
                            '&:hover': {
                                borderColor: selected ? colors.accent : 'rgba(0,0,0,0.15)',
                                bgcolor: selected ? colors.accent : 'rgba(255,255,255,0.9)',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                            },
                        }}
                    >
                        {selected && (
                            <CheckCircleIcon sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                fontSize: 18,
                                color: '#fff',
                            }} />
                        )}
                        <Box sx={{
                            width: 40, height: 40,
                            borderRadius: '10px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: selected ? 'rgba(255,255,255,0.2)' : 'rgba(59,130,246,0.1)',
                            color: selected ? '#fff' : colors.accent,
                            transition: 'all 0.2s ease',
                        }}>
                            {opt.icon}
                        </Box>
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, fontFamily, mt: 0.5 }}>{opt.label}</Typography>
                        <Typography sx={{ fontSize: '0.75rem', opacity: 0.8, lineHeight: 1.2, fontFamily }}>{opt.description}</Typography>
                    </Box>
                );
            })}
        </Box>
    );
};

// --- The Main Component ---
const WebsitePopup = ({ onSubmit, onClose }) => {
    const theme = useTheme();
    const [webname, setWebname] = useState('');
    const [accountType, setAccountType] = useState('individual');
    const [submissionError, setSubmissionError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setSubmissionError('');

        try {
            await onSubmit(webname, accountType);
        } catch (err) {
            console.error("Submission failed in WebsitePopup:", err);
            setSubmissionError(err.message || "An unexpected error occurred. Please try again.");
            setIsLoading(false);
        }
    };

    const isSubmitDisabled = isLoading;

    return (
        <Box
            onClick={onClose}
            sx={{
                position: 'fixed',
                top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(10, 22, 41, 0.4)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000,
                backdropFilter: 'blur(8px)',
                animation: 'fadeIn 0.3s ease-out',
                '@keyframes fadeIn': {
                    from: { opacity: 0 },
                    to: { opacity: 1 },
                },
            }}
        >
            <Box
                onClick={(e) => e.stopPropagation()}
                sx={{
                    width: '100%',
                    maxWidth: 440,
                    mx: 2,
                    p: { xs: 3, sm: 4 },
                    borderRadius: '20px',
                    bgcolor: 'rgba(255, 255, 255, 0.75)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255,255,255,0.6)',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.12), 0 0 0 1px rgba(255,255,255,0.3) inset',
                    animation: 'slideUp 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)',
                    '@keyframes slideUp': {
                        from: { transform: 'translateY(20px)', opacity: 0 },
                        to: { transform: 'translateY(0)', opacity: 1 },
                    },
                }}
            >
                {/* Header */}
                <Box sx={{ textAlign: 'center', mb: 2.5 }}>
                    <Box sx={{
                        width: 48, height: 48, borderRadius: '14px', mx: 'auto', mb: 1.5,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: `0 8px 24px ${theme.palette.primary.main}30`,
                    }}>
                        <LanguageIcon sx={{ color: '#fff', fontSize: 24 }} />
                    </Box>
                    <Typography sx={{
                        fontSize: '1.5rem', fontWeight: 700, color: colors.textPrimary,
                        letterSpacing: '-0.02em', mb: 0.25, fontFamily,
                    }}>
                        Complete Your Setup
                    </Typography>
                    <Typography sx={{ fontSize: '0.8125rem', color: colors.textTertiary, fontFamily }}>
                        Select your account type and optionally provide your website
                    </Typography>
                </Box>

                <Box component="form" onSubmit={handleSubmit} noValidate>
                    {/* Account Type Selector */}
                    <AccountTypeSelector value={accountType} onChange={setAccountType} />

                    <TextField
                        fullWidth
                        size="small"
                        placeholder="e.g., www.yourcompany.com"
                        value={webname}
                        onChange={(e) => setWebname(e.target.value)}
                        error={!!submissionError}
                        helperText={submissionError}
                        autoFocus
                        disabled={isLoading}
                        InputProps={{
                            startAdornment: <LanguageIcon sx={{ fontSize: 18, color: colors.textTertiary, mr: 0.75 }} />,
                        }}
                        sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '14px', fontFamily, bgcolor: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(8px)',
                                border: 'none', outline: 'none',
                                '& .MuiOutlinedInput-notchedOutline': { display: 'none !important' },
                                '& fieldset': { display: 'none !important' },
                                '&.Mui-focused': { bgcolor: 'rgba(255,255,255,1)', boxShadow: `0 0 0 2px ${theme.palette.primary.main}40` },
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                            },
                            '& .MuiInputBase-input': {
                                fontFamily, fontSize: '0.875rem', py: '0.75rem', px: '0.5rem',
                                border: 'none', outline: 'none',
                                '&:focus': { outline: 'none', boxShadow: 'none' },
                                '&::placeholder': { color: colors.textTertiary, opacity: 1 },
                            },
                        }}
                    />

                    <Typography sx={{ fontSize: '0.6875rem', color: colors.textTertiary, fontFamily, mb: 2, textAlign: 'center' }}>
                        Website is optional — you can update it later in settings
                    </Typography>

                    <Button
                        type="submit"
                        fullWidth
                        disabled={isSubmitDisabled}
                        sx={{
                            py: '0.75rem', fontFamily, fontSize: '0.9375rem', fontWeight: 600, textTransform: 'none',
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                            color: '#fff', borderRadius: '14px', height: 46, gap: 1,
                            boxShadow: `0 4px 16px ${theme.palette.primary.main}40`,
                            transition: 'all 0.25s ease',
                            '&:hover': {
                                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                boxShadow: `0 6px 20px ${theme.palette.primary.main}50`,
                                transform: 'translateY(-1px)',
                            },
                            '&:disabled': { opacity: 0.7 },
                        }}
                    >
                        {isLoading && <CircularProgress size={18} sx={{ color: '#fff' }} />}
                        {isLoading ? 'Setting up...' : 'Continue'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default WebsitePopup;
